/**
 * Угода кодування тому в ChapterItem.chapterNumber, спільна для плагінів,
 * сервера (src/lib/chapter-volume.ts, routes/pretty.ts — резолвінг
 * /volNchapterM) і клієнта (цей файл — той самий, тримати синхронізовано).
 *
 * Якщо джерело підтримує томи, плагін кодує номер тому у старший розряд:
 *   chapterNumber = volume * VOLUME_BASE + <номер розділу в межах тому>
 * (див. zenko-n.ts/honeymanga-n.ts/mangainua.ts/bakainua.ts). Без цього
 * кодування "Розділ 1" різних томів мав би однаковий chapterNumber — і
 * порівняння/сортування розділів наскрізним номером (unread-бейдж,
 * перенесення прогресу при імпорті бекапу чи зміні джерела —
 * src/lib/chapters.ts:sortChaptersAscending, src/routes/library.ts:maxNum)
 * плуталося б між томами. Якщо том невідомий — chapterNumber лишається
 * просто "голим" номером розділу (без кодування), як і раніше.
 *
 * VOLUME_BASE підібраний з запасом (реальні тайтли рідко мають 100 000+
 * розділів в одному томі).
 */
export const VOLUME_BASE = 100_000;

export interface DecodedChapterNumber {
  volume?: number;
  chapterInVolume: number;
}

/** chapterNumber → { volume?, chapterInVolume } — не намагається вгадати:
 * якщо число менше VOLUME_BASE, тому просто немає (джерело його не дає). */
export function decodeChapterNumber(n: number): DecodedChapterNumber {
  if (n >= VOLUME_BASE) {
    const volume = Math.floor(n / VOLUME_BASE);
    // округлення проти артефактів плаваючої коми (0.1+0.2 ≠ 0.3 і т.п.)
    const chapterInVolume = Math.round((n - volume * VOLUME_BASE) * 1e6) / 1e6;
    return { volume, chapterInVolume };
  }
  return { chapterInVolume: n };
}

/** Зворотне до decodeChapterNumber — використовується під час резолвінгу
 * /volNchapterM: з чисел тому й розділу відновлює те саме chapterNumber,
 * яке чекаємо знайти в кеші розділів. */
export function encodeChapterNumber(volume: number | undefined, chapterInVolume: number): number {
  return volume !== undefined ? volume * VOLUME_BASE + chapterInVolume : chapterInVolume;
}

/** Число → рядок для URL без зайвих нулів після коми ("1" не "1.000000", але "1.5" лишається "1.5"). */
export function formatChapterUrlNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1e6) / 1e6);
}

/** ChapterItem.chapterNumber → суфікс короткого посилання: "/chapterM" або,
 * якщо джерело кодує том, "/volNchapterM". Спільна для всіх місць клієнта,
 * що будують посилання на розділ (novel.ts, reader.ts, updates.ts, history.ts). */
export function chapterUrlSuffix(chapterNumber: number): string {
  const { volume, chapterInVolume } = decodeChapterNumber(chapterNumber);
  const chStr = formatChapterUrlNumber(chapterInVolume);
  return volume !== undefined ? `/vol${volume}chapter${chStr}` : `/chapter${chStr}`;
}
